import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { Table as AntTable, Alert, Button, Empty, Spin } from 'antd';
import Chart from 'react-apexcharts';
import Card from '../../components/bootstrap/card';
import { dutySavingSections } from './dutySavingSummaryData';

const cardStyle = {
  border: '1px solid #e9ecef',
  borderRadius: '8px',
  overflow: 'hidden'
};

const headerStyle = {
  backgroundColor: '#f8f9fa',
  borderBottom: '1px solid #e9ecef',
  padding: '10px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px'
};

const currencyTitle = (title, currency) => currency === 'INR'
  ? title.replace(/FCC/g, 'INR').replace(/M\$/g, 'Million INR')
  : title;

const formatAmount = (value, decimals, currency) => `${currency === 'INR' ? '₹' : '$'} ${Number(value).toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US', {
  minimumFractionDigits: decimals,
  maximumFractionDigits: decimals
})}`;

const buildColumns = (columns, currency) => columns.map(column => {
  const { decimals, totalDecimals, percentageDecimals, children, ...tableColumn } = column;
  tableColumn.title = currencyTitle(tableColumn.title, currency);
  if (children) return { ...tableColumn, children: buildColumns(children, currency) };
  if (percentageDecimals !== undefined) {
    return { ...tableColumn, render: value => `${Number(value).toFixed(percentageDecimals)}%` };
  }
  if (decimals !== undefined) {
    return {
      ...tableColumn,
      align: 'center',
      render: (value, record) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {formatAmount(value, record.key === 'total' ? (totalDecimals ?? decimals) : decimals, currency)}
        </span>
      )
    };
  }
  return tableColumn;
});

const DutySavingSummary = ({ data, loading, error, onRetry, currency = 'USD' }) => {
  if (loading) {
    return (
      <div role="status" className="text-center p-5">
        <Spin size="large" />
        <div className="mt-2">Loading Duty Saving Report...</div>
      </div>
    );
  }
  if (error) {
    return <Alert type="error" showIcon message={error} action={<Button onClick={onRetry}>Retry</Button>} />;
  }
  const currencyData = currency === 'INR' ? data?.inr : data;
  const rows = currencyData?.dashboard_summary || [];
  if (!rows.length) {
    return <Empty className="p-4" description="No duty saving data available for the selected company and date range." />;
  }
  const refundRows = rows.filter(row => row.key !== 'total');

  return (
    <div>
      <style>{`
        /* Bold the Grand Total row */
        .dash-summary-table .ant-table-tbody > tr.ant-table-summary-row > td,
        .dash-summary-table .ant-table-tbody > tr.ant-table-summary-row > td span {
          font-weight: 700 !important;
        }
      `}</style>
      <div className="d-flex align-items-center flex-wrap gap-2 mb-3" style={{ padding: '0 12px' }}>
        <h5 className="mb-0" style={{ fontSize: '14px', fontWeight: 700 }}>Duty Saving Report</h5>
        <span className="text-muted" style={{ fontSize: '12px' }}>
          Amounts in {currency === 'INR' ? 'INR' : 'USD (FCC)'}; the trade benefits summary is shown in millions of {currency}.
        </span>
      </div>
      <Row className="g-3 align-items-start" style={{ margin: 0 }}>
        <Col xs={12} md={7} className="d-flex flex-column gap-3" style={{ minWidth: 0 }}>
          {dutySavingSections.map(section => (
            <Card key={section.key} className="mb-0" style={cardStyle}>
              <div style={headerStyle}>
                <div style={{ width: '4px', height: '14px', backgroundColor: '#3a57e8', borderRadius: '2px', flexShrink: 0 }} />
                <h6 className="mb-0" style={{ fontWeight: 700, fontSize: '13.5px', color: '#4b5563' }}>{currencyTitle(section.title, currency)}</h6>
              </div>
              <Card.Body className="p-0">
                <AntTable
                  className="dash-summary-table m-0"
                  columns={buildColumns(section.columns, currency)}
                  dataSource={section.key === 'trade-benefits-millions' ? currencyData.trade_benefits_millions : rows}
                  pagination={false}
                  size="small"
                  bordered
                  tableLayout="fixed"
                  scroll={{ x: section.width }}
                  rowClassName={record => record.key === 'total' ? 'ant-table-summary-row' : ''}
                />
              </Card.Body>
            </Card>
          ))}
        </Col>
        <Col xs={12} md={5} style={{ minWidth: 0 }}>
          <Card className="mb-0" style={cardStyle}>
            <div style={headerStyle}>
              <div style={{ width: '4px', height: '14px', backgroundColor: '#3a57e8', borderRadius: '2px', flexShrink: 0 }} />
              <h6 className="mb-0" style={{ fontWeight: 700, fontSize: '13.5px', color: '#4b5563' }}>
                Total Refund Benefit by Business Unit ({currency === 'INR' ? 'INR' : 'FCC'})
              </h6>
            </div>
            <Card.Body className="p-0">
              <div className="p-2">
                <Chart
                  type="bar"
                  height={280}
                  options={{
                    chart: { toolbar: { show: false } },
                    colors: ['#3a57e8'],
                    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '45%' } },
                    dataLabels: { enabled: false },
                    xaxis: {
                      categories: refundRows.map(row => row.business_unit),
                      labels: { formatter: value => `${currency === 'INR' ? '₹' : '$'} ${Number(value).toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US', { notation: 'compact' })}` }
                    },
                    tooltip: { y: { formatter: value => formatAmount(value, 0, currency) } },
                    grid: { borderColor: '#e9ecef' }
                  }}
                  series={[{ name: 'Total Refund Benefit', data: refundRows.map(row => row.refund) }]}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DutySavingSummary;
